// WHICH PRINTED CODE THIS SCAN CAME FROM (TW-0190, 2026-09-17).
//
// Chris, on tagging: "we could have UTMs in the QRs where we don't have them, to know if/when they print or
// screenshot. but also maybe we don't have to be too neurotic about it." So this is ONE dimension, not a
// UTM set: newly printed placards encode `<shop>.waivers.tattoo/?qr` and the cards we mail encode
// `?qr=mail`, and the beacon's first event carries `via` — "qr" · "mail" · "screen" · absent.
//
// THE SCREEN'S OWN WORD (2026-09-30). Chris, that night: "more fidelity on the QR code and scanning is nearly
// always better ... database rows are cheap." — beside, not instead of, the "not too neurotic" above: still ONE
// dimension, one more word in it. The code the shop app DRAWS on its QR screen (apps/app/components/Placard.tsx)
// encodes `?qr=screen`, so a screenshot of that screen, held up at a counter or printed from a camera roll, reads
// "screen" here; the printed sheet, the downloaded PNG and the PNG the share sheet sends are the artifacts that get
// printed or AirDropped as a file, and they encode `?qr`. Until this day the screen's code and the PNG carried NO
// tag at all, so a scan of either landed in "absent" beside the thousands printed since 2023.
//
// ⚠ THE TAG IS INERT TO THE CLIENT, AND THAT IS THE WHOLE CONTRACT. Thousands of QR codes printed since
// 2023 carry no tag at all and must behave EXACTLY as they always have, which they do: no tag reads as
// `undefined` and nothing downstream branches on it. Nothing here changes a screen, a field, a language, a
// price or a submission — the value reaches the beacon and stops. A person standing at a counter cannot
// tell a tagged code from an untagged one, and that is the point.
//
// THE QUERY SURVIVES A MOVED SHOP. A shop that renamed keeps its old subdomain working through a
// CloudFront viewer-request function, and that function rebuilds the query string on the 302 — including a
// VALUELESS parameter, which is the shape `?qr` is
// (infrastructure/prod/functions/redirect-moved-shop-subdomains/index.js `queryString`; its --local suite
// holds both spellings). So a card printed for a shop that later moves still lands, still tagged.
//
// THE SHOP'S OWN PAGE FOR TEXTS READS THE SAME WORDS (TW-0320 slice 2, 2026-10-17). Its QR code on the shop app's QR
// screen encodes `<shop>.waivers.tattoo/texts?qr=screen`, and its printed sheet and PNG `…/texts?qr`: the same two words,
// on another PATH. Every row that keeps the word keeps the page beside it (the page's own beacon events, `texts_open` and
// the rest, never `form_open`; and the client's record of the yes, `consent.text_signup {page, via}`), so a scan of the
// texting sheet, of the texting code on the screen and of the waiver form's placard are three different rows, the rule
// of 2026-09-30 kept, with no word that only repeats the path (a companion call, the builder of the page: one word in this
// list, form_beacon.py's VIA and Promotions.TextSignup's @via overrules it). This list and the server's stay as they are.
//
// A CLOSED VOCABULARY, twice. Anyone can type anything after `?qr=`, and `via` rides to the family's shared
// analytics intake, so the value is checked against a fixed list here AND again on the server
// (services/waiver-api/consolidated_api/form_beacon.py `VIA`). Anything else is not sanitized — it is gone,
// the same friction the event names and `route` already carry.

/** The parameter a code carries. Bare (`?qr`) for a placard, `?qr=mail` for a mailed card, `?qr=screen` for the
 *  shop app's QR screen. */
export const QR_KEY = "qr";

/** WHERE THE SCAN CAME FROM. "qr" = a placard printed from the shop app or the site, or the PNG of it; "mail" =
 *  the card we posted; "screen" = the code the shop app draws on its QR screen, scanned off the screen or off a
 *  screenshot of it (2026-09-30). A new value is a new kind of code, and it goes here, in form_beacon.py's `VIA`,
 *  and in whatever draws it — in one commit, or the server drops it. The shop app's lib/qr-tag.test.ts reads this
 *  file and form_beacon.py and goes red when the app draws a word either of them would drop. */
export const VIA_PLACARD = "qr";
export const VIA_MAIL = "mail";
export const VIA_SCREEN = "screen";
/** TW-0517 (2026-10-27): the cold placard, mailed folded to a shop that never signed up, its page minted the day it was
 *  ordered (scripts/lib_prospect_postcards.py): `?qr=cold`, so a scan of it is told from a placard the shop printed and from
 *  the card a signed-up shop is mailed. Inert to the client, like every word here. */
export const VIA_COLD = "cold";
/** THE SHOP'S THANK-YOU POSTCARD TO A CLIENT (TW-0408, 2026-10-27): its code opens our door, which counts the scan on the
 *  card's own row and sends the phone on with `qr=postcard` wherever that is one of our pages
 *  (services/waiver-api/consolidated_api/postcard_scan.py, postcards.tagged). A different printed thing from `mail`, the card
 *  WE post a shop: that one opens this form, this one a page the shop picked. */
export const VIA_POSTCARD = "postcard";
/** The whole vocabulary. `beacon.ts` `clean()` checks against THIS rather than a second copy, so the
 *  reader and the filter cannot drift; the server keeps its own on purpose (form_beacon.py `VIA`), because
 *  two independent filters is the point there. */
export const VIA_VALUES: readonly string[] = [VIA_PLACARD, VIA_MAIL, VIA_SCREEN, VIA_COLD, VIA_POSTCARD];

/** `via` for this page load, or undefined. `?qr` with no value is a placard; `?qr=mail` is a mailed card;
 *  `?qr=screen` is the shop app's QR screen; no parameter, or a value this list has not learned, is undefined —
 *  which is what every code printed before 2026-09-17 reads as, and it is not an error. */
export function readViaFromUrl(
  search: string = typeof window === "undefined" ? "" : window.location.search
): string | undefined {
  try {
    const raw = new URLSearchParams(search).get(QR_KEY);
    if (raw === null) return undefined;
    // `?qr` alone parses to the empty string, and it means the plainest thing a tag can mean: a placard.
    const value = raw === "" ? VIA_PLACARD : raw;
    return VIA_VALUES.includes(value) ? value : undefined;
  } catch {
    /* a malformed URL is not a scan worth failing over */
    return undefined;
  }
}

// Read ONCE, at module load, for the same reason the beacon mints its visit id once: the unit is this page
// load. The form is a single-page app and nothing navigates before the first event, so re-reading would
// cost a URLSearchParams on a page whose paint speed is the product and answer the same thing every time.
const via = readViaFromUrl();

/** What the first beacon event carries — `{ via }` when this scan came from a tagged code, and nothing at
 *  all when it did not. Spread into the meta, so an untagged scan's row is byte-for-byte the row it was. */
export function viaFields(): { via?: string } {
  return via ? { via } : {};
}
